import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getUserData } from '../../helpers/user';
import { notify } from '../../helpers/utils';
import {
  User, Calendar, Tag, Zap, DollarSign,
  MessageSquare, CheckCircle, Loader, AlertTriangle
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)', surf3: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', amber: '#FBBF24', red: '#F87171',
  purple: '#A78BFA', green: '#34D399', font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .cso-input,.cso-select,.cso-textarea {
    width:100%;background:var(--nd-w05);border:1px solid var(--nd-w10);
    border-radius:9px;color:var(--nd-text);font-size:13px;font-family:'DM Sans',sans-serif;
    outline:none;transition:border-color 0.15s,background 0.15s;
  }
  .cso-input,.cso-select{padding:9px 12px 9px 34px;height:40px;}
  .cso-textarea{padding:9px 12px 9px 34px;resize:vertical;min-height:72px;}
  .cso-input[readonly]{opacity:0.5;cursor:default;}
  .cso-input::placeholder,.cso-textarea::placeholder{color:var(--nd-tx22);}
  .cso-input:focus,.cso-select:focus,.cso-textarea:focus{border-color:rgba(59,130,246,0.55);background:rgba(59,130,246,0.05);}
  .cso-select{appearance:none;cursor:pointer;color-scheme:dark;}
  .cso-select option{background:var(--nd-surface-2);color:var(--nd-text);}
  .cso-label{display:block;font-size:10px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:var(--nd-tx40);margin-bottom:5px;}
  .cso-btn{display:inline-flex;align-items:center;gap:7px;padding:10px 24px;border-radius:9px;border:none;background:linear-gradient(135deg,#3B82F6,#22D3A5);color:var(--nd-text-strong);font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 14px rgba(59,130,246,0.28);transition:opacity 0.15s;}
  .cso-btn:hover:not(:disabled){opacity:.88;}
  .cso-btn:disabled{opacity:.45;cursor:not-allowed;}
  ::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--nd-w10);border-radius:4px}
`;

const Field = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label className="cso-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);
const TextareaField = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label className="cso-label">{label}</label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} color={iconColor} style={{ position:'absolute',left:11,top:11,pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);
const Grid2 = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'0 20px' }}>{children}</div>
);
const Section = ({ Icon, iconColor, title, delay = 0, children }) => (
  <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden', marginBottom:16, animation:`fadeUp 0.3s ease ${delay}s both` }}>
    <div style={{ padding:'12px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:9 }}>
      <div style={{ width:26,height:26,borderRadius:7,background:`${iconColor}15`,border:`1px solid ${iconColor}30`,display:'flex',alignItems:'center',justifyContent:'center' }}>
        <Icon size={13} color={iconColor} />
      </div>
      <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{title}</span>
    </div>
    <div style={{ padding:'18px 20px 4px' }}>{children}</div>
  </div>
);

// ── Business logic preserved 100% ────────────────────────────────
const normalizeFrequency = (label) => {
  switch (label.toLowerCase()) {
    case 'single wash': return 'single';
    case 'monthly package': return 'monthly';
    case 'quarterly package': return 'quarterly';
    case 'half yearly': return 'halfyearly';
    case 'yearly': return 'yearly';
    default: return '';
  }
};
const formatDate = (isoDate) => {
  const d = new Date(isoDate);
  return `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
};
const calculatePrice = (kw, frequency) => {
  const priceMatrix = {
    single:    [4000,5000,6000,8000,12000,15000],
    monthly:   [6000,8000,10000,14000,20000,25000],
    quarterly: [17250,21000,27000,39000,54000,69000],
    halfyearly:[33000,40500,52500,75000,105000,135000],
    yearly:    [33000,40500,52500,75000,105000,135000],
  };
  let ti = kw<=10?0:kw<=15?1:kw<=20?2:kw<=30?3:kw<=40?4:5;
  return priceMatrix[frequency.toLowerCase()]?.[ti] || 0;
};

// ══════════════════════════════════════════════════════════════════
const CreateSalesOrder = () => {
  const { id: clientReff } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading]                 = useState(false);
  const [customer, setCustomer]               = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [customerError, setCustomerError]     = useState(null);
  const [agentName, setAgentName]             = useState('');
  const [formData, setFormData] = useState({
    service_type:'', system_size:'', frequency:'', discount:'',
    total_amount:'', final_amount:'', cleaning_date:'', commentbox:'',
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      setCustomerLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/customers/${clientReff}`);
        setCustomer(res.data);
      } catch (error) {
        notify('error', `Failed to fetch customer: ${error.response?.data?.message || error.message}`);
        console.error('Failed to fetch customer:', error);
        setCustomerError('Error fetching customer details. Please try again.');
      } finally { setCustomerLoading(false); }
    };
    const user = getUserData();
    console.log(user);
    setAgentName(user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Unknown Agent');
    fetchCustomer();
  }, [clientReff]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let upd = { ...formData, [name]: value };
    if (name === 'system_size' || name === 'frequency') {
      const kw = parseFloat(name === 'system_size' ? value : formData.system_size);
      const freq = name === 'frequency' ? value : formData.frequency;
      if (!isNaN(kw) && freq) {
        const price = calculatePrice(kw, normalizeFrequency(freq));
        upd.total_amount = price;
        upd.final_amount = price - (parseFloat(upd.discount) || 0);
      }
    }
    if (name === 'final_amount') {
      const final = Math.floor(parseFloat(value));
      const total = parseFloat(formData.total_amount);
      if (!isNaN(final) && !isNaN(total)) upd.discount = total - final;
    }
    setFormData(upd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/sales/create-sales-orders`, {
        ...formData, cleaning_date: formatDate(formData.cleaning_date),
        cid: clientReff, agent_name: agentName, status: 'Pending', so_completed: 'No',
      });
      navigate('/sales/list-sales-orders');
    } catch (error) {
      notify('error', `Failed to create sales order: ${error.response?.data?.message || error.message}`);
      console.error('Error creating sales order', error);
    }
    setLoading(false);
  };

  return (
    <div style={{ background:T.bg, fontFamily:T.font, color:T.text, padding:'14px 20px' }}>
      <style>{STYLES}</style>

      {/* Page header */}
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:16, marginBottom:18, overflow:'hidden' }}>
        <div style={{ padding:'18px 24px' }}>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:'0.18em', marginBottom:5 }}>iNOVAA · SALES</div>
          <div style={{ fontSize:26, fontWeight:800, color:'var(--nd-text-strong)', letterSpacing:'-0.03em', lineHeight:1 }}>Create Sales Order</div>
          <div style={{ fontSize:12, color:T.muted, marginTop:5 }}>New order for Customer #{clientReff}</div>
        </div>
        <div style={{ height:2, background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* Error banner */}
      {customerError && (
        <div style={{ background:'rgba(248,113,113,0.10)',border:'1px solid rgba(248,113,113,0.28)',borderRadius:10,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:9,color:T.red,fontSize:13 }}>
          <AlertTriangle size={15} /> {customerError}
        </div>
      )}

      {/* Customer info */}
      {customerLoading ? (
        <div style={{ display:'flex',alignItems:'center',justifyContent:'center',padding:'40px',gap:12 }}>
          <Loader size={20} color={T.blue} style={{ animation:'spin 0.8s linear infinite' }} />
          <span style={{ color:T.muted, fontSize:13 }}>Loading customer…</span>
        </div>
      ) : customer ? (
        <Section Icon={User} iconColor={T.blue} title="Customer Information" delay={0}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'0 32px', paddingBottom:8 }}>
            <div>
              {[
                { label:'Name',     value:customer.client_name,   color:T.text,  bold:true },
                { label:'Phone',    value:customer.phone_number },
                { label:'Location', value:`${customer.client_city||'—'}, ${customer.client_country||'—'}` },
                { label:'Priority', value:customer.priority===1?'High':'Normal', color:customer.priority===1?T.red:T.green },
              ].map(({ label, value, color, bold }) => (
                <div key={label} style={{ display:'flex', gap:8, marginBottom:8, fontSize:12 }}>
                  <span style={{ color:T.muted, minWidth:64 }}>{label}</span>
                  <span style={{ color:color||T.text, fontWeight:bold?700:500 }}>{value}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4 }}>Address</div>
              <div style={{ fontSize:12, color:T.text, wordBreak:'break-word', marginBottom:8 }}>{customer.address||'—'}</div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4 }}>Pin Location</div>
              <div style={{ fontSize:11, color:T.teal, wordBreak:'break-all' }}>{customer.pin_location||'—'}</div>
            </div>
            <div>
              <div style={{ marginBottom:10 }}>
                <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:5, fontSize:10, fontWeight:700,
                  background:customer.client_type===1?'rgba(59,130,246,0.12)':'rgba(34,211,165,0.12)',
                  color:customer.client_type===1?T.blue:T.teal,
                  border:`1px solid ${customer.client_type===1?'rgba(59,130,246,0.28)':'rgba(34,211,165,0.28)'}` }}>
                  {customer.client_type===1?'🏠 Residential':'🏢 Commercial'}
                </span>
              </div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:3 }}>Date Added</div>
              <div style={{ fontSize:12, color:T.text, fontFamily:T.mono }}>{new Date(customer.date_added).toLocaleDateString()}</div>
            </div>
          </div>
        </Section>
      ) : (
        <div style={{ background:'rgba(248,113,113,0.08)',border:'1px solid rgba(248,113,113,0.22)',borderRadius:10,padding:'20px',textAlign:'center',color:T.red,marginBottom:16 }}>
          No customer data found.
        </div>
      )}

      {/* Sales order form */}
      {!customerLoading && !customerError && customer && (
        <form onSubmit={handleSubmit}>
          <Section Icon={Zap} iconColor={T.teal} title="Add Sales Details" delay={0.06}>
            <Grid2>
              <Field label="System Size (KW)" icon={Zap} iconColor={T.teal}>
                <input className="cso-input" type="text" name="system_size" value={formData.system_size} onChange={handleChange} placeholder="e.g. 10" required />
              </Field>
              <Field label="Frequency" icon={Tag} iconColor={T.purple}>
                <select className="cso-select" name="frequency" value={formData.frequency} onChange={handleChange} required>
                  <option value="">Select Frequency</option>
                  <option value="Single Wash">Single Wash</option>
                  <option value="Monthly Package">Monthly Package</option>
                  <option value="Quarterly Package">Quarterly Package</option>
                  <option value="Half Yearly Package">Half Yearly</option>
                  <option value="Yearly Package">Yearly Contract</option>
                </select>
              </Field>
              <Field label="Total Amount (auto)" icon={DollarSign} iconColor={T.muted}>
                <input className="cso-input" type="number" value={formData.total_amount||''} readOnly />
              </Field>
              <Field label="Discount (auto)" icon={DollarSign} iconColor={T.amber}>
                <input className="cso-input" type="text" name="discount" value={formData.discount} readOnly />
              </Field>
              <Field label="Final Amount" icon={DollarSign} iconColor={T.teal}>
                <input className="cso-input" type="number" step="1" min="0" name="final_amount" value={formData.final_amount} onChange={handleChange} required />
              </Field>
              <Field label="Cleaning Date" icon={Calendar} iconColor={T.blue}>
                <input className="cso-input" type="date" name="cleaning_date" value={formData.cleaning_date} onChange={handleChange} required style={{ colorScheme:'dark' }} />
              </Field>
            </Grid2>
            <TextareaField label="Comments" icon={MessageSquare} iconColor={T.muted}>
              <textarea className="cso-textarea" name="commentbox" value={formData.commentbox} onChange={handleChange} rows={2} placeholder="Optional notes…" />
            </TextareaField>
          </Section>

          {/* Price summary */}
          {formData.total_amount > 0 && (
            <div style={{ background:'rgba(34,211,165,0.06)',border:'1px solid rgba(34,211,165,0.18)',borderRadius:10,padding:'10px 18px',marginBottom:16,display:'flex',gap:28,flexWrap:'wrap' }}>
              {[
                { label:'System Price', value:`Rs ${Number(formData.total_amount).toLocaleString()}`,    color:T.muted },
                { label:'Discount',     value:`Rs ${Number(formData.discount||0).toLocaleString()}`,     color:T.amber },
                { label:'Final',        value:`Rs ${Number(formData.final_amount||0).toLocaleString()}`, color:T.teal  },
              ].map(({ label, value, color }) => (
                <div key={label}>
                  <div style={{ color:T.muted, fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:2 }}>{label}</div>
                  <div style={{ color, fontWeight:800, fontFamily:T.mono, fontSize:14 }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', paddingBottom:4 }}>
            <button type="submit" className="cso-btn" disabled={loading}>
              {loading
                ? <><Loader size={13} style={{ animation:'spin 0.8s linear infinite' }} /> Creating…</>
                : <><CheckCircle size={13} /> Create Order</>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateSalesOrder;